import React, { useState, useEffect } from "react";
import { Modal, Form, Input, Select, Radio, Switch, message } from "antd";
import {
  createAttributeMapping,
  updateAttributeMapping,
  getAttributeCapabilities,
} from "../../../services/integrations/klaviyoAttributeMappingsService";

const { Option } = Select;

export default function AttributeMappingModal({ visible, onClose, onSaved, editMapping }) {
  const [form] = Form.useForm();
  const [capabilities, setCapabilities] = useState([]);
  const [direction, setDirection] = useState("OUTBOUND");
  const [enabled, setEnabled] = useState(true);
  const [saving, setSaving] = useState(false);
  const isEdit = !!editMapping;

  useEffect(() => {
    if (visible) {
      loadCapabilities();
      if (editMapping) {
        form.setFieldsValue({
          dataClassAttributeId: editMapping.dataClassAttributeId,
          vendorField:
            editMapping.providerFieldOutbound || editMapping.providerFieldInbound || "",
          valueExpression: editMapping.valueExpression || "",
          typeIntent: editMapping.typeIntent || "SCALAR",
        });
        setDirection(editMapping.direction || "OUTBOUND");
        setEnabled(editMapping.enabled !== false);
      } else {
        form.resetFields();
        setDirection("OUTBOUND");
        setEnabled(true);
      }
    }
  }, [visible, editMapping, form]);

  const loadCapabilities = async () => {
    try {
      const res = await getAttributeCapabilities();
      setCapabilities(Array.isArray(res) ? res : res?.capabilities || []);
    } catch (err) {
      // capabilities may not be available
    }
  };

  const handleSave = async () => {
    try {
      const values = await form.validateFields();
      setSaving(true);

      const body = {
        dataClassAttributeId: values.dataClassAttributeId,
        direction,
        vendorField: values.vendorField,
        typeIntent: direction === "OUTBOUND" ? (values.typeIntent || "SCALAR") : null,
        enabled,
      };

      if (isEdit) {
        await updateAttributeMapping(editMapping.id, body);
        message.success("Attribute mapping updated");
      } else {
        await createAttributeMapping(body);
        message.success("Attribute mapping created");
      }
      onSaved();
      onClose();
    } catch (err) {
      if (err?.errorFields) return;
      message.error("Failed to save attribute mapping");
    }
    setSaving(false);
  };

  // Build display label for existing mapping attribute that may not be in capabilities
  const getAttributeLabel = () => {
    if (editMapping?.attributeName) {
      return `${editMapping.attributeName} (${editMapping.dataClassAttributeId})`;
    }
    return null;
  };

  return (
    <Modal
      title={isEdit ? "Edit Attribute Mapping" : "Add Attribute Mapping"}
      open={visible}
      onCancel={onClose}
      onOk={handleSave}
      okText="Save"
      cancelText="Cancel"
      width={520}
      confirmLoading={saving}
      okButtonProps={{ className: "standard-button primary-orange-button" }}
      cancelButtonProps={{ className: "standard-button secondary-orange-button" }}
    >
      <Form form={form} layout="vertical" style={{ marginTop: 16 }}>
        <Form.Item
          label="Polly Attribute"
          name="dataClassAttributeId"
          rules={[{ required: true, message: "Select an attribute" }]}
        >
          <Select
            placeholder="Select attribute"
            className="standard-input"
            showSearch
            optionFilterProp="children"
            disabled={isEdit}
          >
            {/* Show the existing attribute if editing and it's not in capabilities */}
            {isEdit && editMapping?.dataClassAttributeId &&
              !capabilities.some((c) => c.id === editMapping.dataClassAttributeId) && (
                <Option
                  key={editMapping.dataClassAttributeId}
                  value={editMapping.dataClassAttributeId}
                >
                  {getAttributeLabel()}
                </Option>
              )}
            {capabilities.map((cap) => (
              <Option key={cap.id} value={cap.id}>
                {cap.name || cap.description || cap.attributeName} ({cap.id})
              </Option>
            ))}
          </Select>
        </Form.Item>

        <Form.Item label="Direction">
          <Radio.Group
            value={direction}
            onChange={(e) => setDirection(e.target.value)}
          >
            <Radio.Button value="OUTBOUND">Outbound</Radio.Button>
            <Radio.Button value="INBOUND">Inbound</Radio.Button>
          </Radio.Group>
        </Form.Item>

        <Form.Item
          label="Vendor Field"
          name="vendorField"
          rules={[{ required: true, message: "Enter the Klaviyo profile property name" }]}
        >
          <Input
            className="standard-input"
            placeholder="e.g., watch_color"
          />
        </Form.Item>

        <Form.Item
          label="Value Expression"
          name="valueExpression"
        >
          <Input
            className="standard-input"
            placeholder="(identity -- attribute value passed as-is)"
          />
        </Form.Item>

        {direction === "OUTBOUND" && (
          <Form.Item label="Type Intent" name="typeIntent" initialValue="SCALAR">
            <Radio.Group>
              <Radio.Button value="SCALAR">scalar</Radio.Button>
              <Radio.Button value="APPEND">append</Radio.Button>
              <Radio.Button value="REMOVE">remove</Radio.Button>
              <Radio.Button value="REPLACE_LIST">replace_list</Radio.Button>
            </Radio.Group>
          </Form.Item>
        )}

        <Form.Item label="Enabled">
          <Switch checked={enabled} onChange={setEnabled} />
        </Form.Item>
      </Form>
    </Modal>
  );
}
